<template>
  <div class="object-program">
    <h3>{{ $t("attr_value_program_title") }}</h3>

    <label class="app__label" for="objectProgramLanguage">
      {{ $t("object_attr_lang") }}
    </label>
    <el-select
      id="objectProgramLanguage"
      v-model="currentLanguage"
      filterable
      :disabled="isEditedMode"
      @change="changeAttributeLang"
    >
      <el-option
        v-for="item in languages"
        :key="item.id"
        :label="item.name"
        :value="item.id"
      >
      </el-option>
    </el-select>

    <el-table
      class="object-program__table"
      v-loading="loading"
      :data="tableData"
      style="width: 100%;"
    >
      <el-table-column :label="$t('attr_value_program_tbl_day')" prop="day">
        <template slot-scope="scope">
          <el-input-number
            v-model="scope.row.day"
            :min="1"
            @change="changeDay(scope.row, scope.$index)"
          ></el-input-number>
        </template>
      </el-table-column>

      <el-table-column :label="$t('attr_value_program_tbl_city')" prop="cities">
        <template slot-scope="scope">
          <el-select
            v-model="scope.row.cities"
            multiple
            filterable
            @change="changeCities(scope.row)"
          >
            <el-option
              v-for="item in cities"
              :key="item.id"
              :label="item.name"
              :value="item.id"
            ></el-option>
          </el-select>
        </template>
      </el-table-column>

      <el-table-column
        :label="$t('attr_value_program_tbl_value')"
        prop="description"
      >
        <template slot-scope="scope">
          <el-button
            :class="{
              'exist-complex-value': scope.row.description[currentLanguage]
            }"
            type="text"
            icon="el-icon-edit"
            @click="changeText(scope.row)"
          >
            {{ linkText(scope.row.description) }}
          </el-button>
        </template>
      </el-table-column>

      <el-table-column width="100" align="center">
        <template slot-scope="scope">
          <el-button
            type="warning"
            icon="el-icon-delete"
            :title="$t('attr_value_program_tbl_delete_title')"
            @click.native.prevent="deleteRow(scope.row.guid)"
          ></el-button>
        </template>
      </el-table-column>
    </el-table>

    <!--
    <div v-if="isEmptyRequaredField" class="object-program__error">
      {{ $t("attr_value_program_requared") }}
    </div>
    -->

    <div class="object-program__action">
      <el-button type="primary" @click="addDay">
        {{ $t("attr_value_program_add") }}
      </el-button>

      <el-button @click="cancel">
        {{ $t("attr_value_program_cancel") }}
      </el-button>

      <el-button type="success" :disabled="disabledSave" @click="save">
        {{ $t("attr_value_program_save") }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapMutations } from "vuex";
import { TextEditor } from "@/mixins";

import { clone } from "@/domain/system/utils";
import * as programService from "@/domain/object/program.service";
import { MODE_INIT, MODE_EDIT /*, MODE_NEW */ } from "@/domain/mode.types";

export default {
  name: "ObjectProgram",
  components: {},
  props: {
    loading: Boolean,
    languages: Array,
    cities: Array
  },
  mixins: [TextEditor],
  data() {
    return {
      tableData: [],
      currentLanguage: "",
      mode: MODE_INIT,
      editedRow: {}
    };
  },
  computed: {
    ...mapState({
      program: state => state.objectComplexValue.programList.program,
      text: state => state.textEditor.text,
      status: state => state.textEditor.status,
      requaredField: state => state.objectComplexValue.requaredField
    }),
    isEditedMode() {
      return this.mode === MODE_EDIT;
    },
    isInitMode() {
      return this.mode === MODE_INIT;
    },
    isEmptyRequaredField() {
      return this.program.length === 0;
    },
    disabledSave() {
      return this.isInitMode || this.isEmptyRequaredField;
    }
  },
  methods: {
    ...mapMutations([
      "appendProgramObject",
      "deleteProgramObject",
      "updateProgramItem",
      "setProgramList"
    ]),
    async addDay() {
      this.setEditedMode();

      this.appendProgramObject(
        programService.getNewDayObject(this.program, this.languages)
      );
    },
    async deleteRow(guid) {
      this.setEditedMode();
      this.deleteProgramObject(guid);
    },
    async setEditedMode() {
      this.mode = MODE_EDIT;
    },
    async setInitMode() {
      this.mode = MODE_INIT;
    },
    async changeDay(row, index) {
      this.setProgramList(
        programService.recalcProgramDays(
          this.tableData.map(t => clone(t)),
          index
        )
      );

      // this.setTableData();
      this.setEditedMode();
    },
    changeCities(row) {
      let cities = [];

      row.cities.forEach(c => {
        cities.push(c);
      });

      this.updateProgramItem({
        guid: row.guid,
        fieldName: "cities",
        value: cities
      });

      this.setEditedMode();
    },
    async changeText(row) {
      this.editedRow = row;
      this.setText(row.description[this.currentLanguage]); // mixins
      this.$emit("changeProgram", this.statusDoneFunc, this.statusCanceledFunc);
    },
    linkText(description) {
      return description &&
        description[this.currentLanguage] &&
        description[this.currentLanguage].length > 0
        ? this.$t("attr_value_program_memo_edit")
        : this.$t("attr_value_program_memo_empty");
    },
    cancel() {
      this.$emit("cancel");
    },
    save() {
      this.setInitMode();
      this.$emit("save", this.program);
    },
    changeAttributeLang() {
      this.$emit("changeCurrentLanguage", this.currentLanguage);
    },
    setCurrentLang() {
      this.currentLanguage = this.$store.state.objectSetting.currentLanguage;
    },
    async setTableData() {
      this.tableData = [];
      this.$nextTick(function () {
        this.tableData = this.program.map(g => clone(g));
      });
    },
    statusDoneFunc(val) {
      this.updateProgramItem({
        guid: this.editedRow.guid,
        fieldName: `description.${this.currentLanguage}`,
        value: val
      });

      this.setTableData();
      this.setEditedMode();
    },
    statusCanceledFunc() {
      this.editedRow = {};
    }
  },
  mounted() {
    this.setCurrentLang();
    this.setTableData();
  },
  watch: {
    program: function () {
      this.setTableData();
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/css/variables.scss";

.object-program {
  padding: 15px 10px;
  height: calc(100% - 30px);
  overflow: auto;

  .object-program__table {
    margin-top: $component_padding_on_top;
    max-height: calc(100% - 90px);
    overflow: auto;

    .object-program__table_req {
      color: $danger_color;
    }
  }

  .object-program__action {
    width: 100%;
    padding: 10px 0;
    text-align: right;
    position: sticky;
    bottom: -22px;
    background-color: white;
    z-index: 4;
  }

  .object-program__error {
    padding-top: $component_padding_on_top;
    color: $danger_color;
    font-size: $main_font_size;
  }
}
</style>
